import React, { useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";

function QuickUnitCreator({ show, onHide, onUnitCreated, defaultType = "metric" }) {
  const [unitName, setUnitName] = useState("");
  const [unitType, setUnitType] = useState(defaultType);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    // Reset error
    setError("");

    // Validate input
    if (!unitName || unitName.trim() === "") {
      setError("Unit name cannot be empty");
      return;
    }

    if (unitName.trim().length > 50) {
      setError("Unit name must be 50 characters or less");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/add-unit`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          unitName: unitName.trim(),
          unitType: unitType.toLowerCase(),
        }),
      });

      if (response.ok) {
        // Fetch the updated units list
        const unitsResponse = await fetch(`${process.env.REACT_APP_API_URL}/units`, {
          credentials: "include",
        });
        
        if (unitsResponse.ok) {
          const units = await unitsResponse.json();
          // Find the newly created unit
          const newUnit = units.find(
            u => u.unit_name.toLowerCase() === unitName.trim().toLowerCase() && 
                 u.unit_source === "User"
          );
          
          if (newUnit) {
            onUnitCreated(newUnit);
            handleClose();
          } else {
            setError("Unit created but not found in the list. Please refresh.");
          }
        }
      } else if (response.status === 400) {
        const message = await response.text();
        setError(message || "Invalid unit name");
      } else {
        setError("Failed to create unit. Please try again.");
      }
    } catch (err) {
      console.error("Error creating unit:", err);
      setError("An error occurred while creating the unit");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUnitName("");
    setUnitType(defaultType);
    setError("");
    onHide();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      handleCreate();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="sm" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create New Unit</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>}
        
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Unit Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., tablespoon, kilogram"
              value={unitName}
              onChange={(e) => setUnitName(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={50}
              autoFocus
            />
            <Form.Text className="text-muted">
              {unitName.length}/50 characters
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Unit Type</Form.Label>
            <Form.Select
              value={unitType}
              onChange={(e) => setUnitType(e.target.value)}
            >
              <option value="metric">Metric</option>
              <option value="traditional">Traditional</option>
              <option value="other">Other</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Choose the measurement system this unit belongs to
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleCreate}
          disabled={isLoading || !unitName.trim()}
        >
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Creating...
            </>
          ) : (
            "Create Unit"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default QuickUnitCreator;